import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FONT_SIZE, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';
import { useNavigation } from '../../contexts/NavigationContext';
import { AppFilterChips, type AppFilterChipOption } from '../common/AppFilterChips';
import { StatusBadge } from '../common/StatusBadge';
import {
  PRICE_VIEW_HINT, PRICE_VIEW_LABEL, PRICE_VIEW_ORDER, PRICE_VIEW_PAGE, type PriceView, type PriceViewCounts, type PriceViewPage,
  STALE_AFTER_MS, isPriceView, isStale, writePriceView,
} from './priceViews';

/** 1분마다 지금 시각 — 신선도 경고가 화면을 켜 둔 채로도 켜지게. */
function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** 오늘이면 HH:mm, 아니면 M/D HH:mm. */
export const formatCollectedAt = (at: Date, now: number): string =>
  dayjs(at).isSame(dayjs(now), 'day') ? dayjs(at).format('HH:mm') : dayjs(at).format('M/D HH:mm');

export interface PriceViewBarProps {
  /** 지금 화면 — 다른 화면의 칩은 그 메뉴로 옮겨 연다. */
  page: PriceViewPage;
  /** 지금 화면에서 고른 칩(없으면 null). */
  view: PriceView | null;
  /** 칩 건수(필터 전 전체) — null 은 이 화면에서 모르는 건수(칸을 비운다). */
  counts: PriceViewCounts;
  /** 이 화면의 칩을 고르거나 끌 때. 주소(`?view=`)는 화면이 맞춘다. */
  onChange: (view: PriceView | null) => void;
  /** 마지막 가격 수집 시각(없으면 '기록 없음'). undefined 면 신선도 줄을 그리지 않는다(아직 못 받음). */
  collectedAt?: Date | null;
  /** 칩 뜻 줄(title) 끝에 붙일 건수 설명(예: '14:02:10 기준' · 'PriceHub 목록을 받지 못해 건수를 모릅니다'). 칩 글은 그대로. */
  countNotes?: Partial<Record<PriceView, string>>;
}

/**
 * 표 위 한 줄 — 저장 필터 칩 다섯(건수) + 마지막 수집 시각(6시간 넘으면 '오래됨', 결정 D10). 가격 목록 개편 §2-3 · §2-6 · PR 3
 * (감사 card-controltower#697). 칩 한 줄이 넘치면 가로 스크롤(390).
 */
export const PriceViewBar: React.FC<PriceViewBarProps> = ({ page, view, counts, onChange, collectedAt, countNotes }) => {
  const t = useThemeTokens();
  const phone = useIsPhone();
  const nav = useNavigation();
  const now = useNow();

  const options: AppFilterChipOption[] = PRICE_VIEW_ORDER.map((v) => {
    const here = PRICE_VIEW_PAGE[v] === page;
    return {
      value: v,
      label: PRICE_VIEW_LABEL[v],
      count: counts[v],
      // 다른 메뉴의 칩은 눌러서 그 메뉴로 간다는 것을 뜻 줄에.
      title: [here ? PRICE_VIEW_HINT[v] : `${PRICE_VIEW_HINT[v]} — 눌러서 그 메뉴로 이동`, countNotes?.[v]].filter(Boolean).join(' · '),
      testId: `price-view-${v}`,
    };
  });

  const change = (next: string[]) => {
    const v = next[0];
    if (!isPriceView(v)) { onChange(null); return; }
    const target = PRICE_VIEW_PAGE[v];
    if (target === page) { onChange(v); return; }
    // 다른 메뉴의 조건 — 주소에 먼저 남기고 옮긴다(옮긴 화면이 처음 그릴 때 주소의 보기로 연다).
    writePriceView(v);
    nav.go(target);
  };

  const stale = collectedAt != null && isStale(collectedAt, now);
  const staleHours = Math.round(STALE_AFTER_MS / 3_600_000);

  return (
    <div
      data-testid="price-view-bar"
      style={{
        display: 'flex', alignItems: phone ? 'stretch' : 'center', flexDirection: phone ? 'column' : 'row',
        gap: phone ? SPACING.xxs : SPACING.md, minWidth: 0, // 아래 표와의 간격은 .ct-table-page(12)가 준다
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <AppFilterChips
          ariaLabel="저장된 보기"
          options={options}
          value={view ? [view] : []}
          onChange={change}
          single
          showCount
          scroll
        />
      </div>
      {collectedAt !== undefined && (
        <div
          data-testid="price-collected-at"
          title={`PriceHub 에서 가격을 받은(① 가격 받기 · 아침 자동 작업) 가장 늦은 시각 — ${staleHours}시간이 지나면 오래됨으로 알립니다`}
          style={{
            display: 'flex', alignItems: 'center', gap: SPACING.xs, flexShrink: 0, flexWrap: 'wrap',
            fontSize: FONT_SIZE.xs, color: stale ? t.warning : t.textSecondary, fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>
            {collectedAt ? `마지막 수집 ${formatCollectedAt(collectedAt, now)}` : '마지막 수집 기록 없음'}
          </span>
          {stale && (
            <StatusBadge axis="work" status="review" label={`오래됨 — ${staleHours}시간 넘음`} testId="price-collected-stale" />
          )}
        </div>
      )}
    </div>
  );
};
